import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import moment from 'moment';
import { I18n, translate } from '@utils/i18n';
import { api } from '../client';
import type {
  AlertCardData,
  AlertIconType,
  AlertSeverity,
  DateGroup
} from '_clean-code/components/AlertCard';

export const NOTIFICATIONS_QUERY_KEY = 'notifications';
export const UNREAD_COUNT_QUERY_KEY = 'unreadNotificationsCount';

export type NotificationStatus = 'read' | 'unread' | 'all';

interface LocalizedText {
  english: string;
  spanish: string;
}

interface RelatedDateRange {
  start: number;
  end?: number;
  shiftId: number;
}

export interface Notification {
  id: string;
  title: LocalizedText;
  alertedAt: string;
  description: LocalizedText;
  machineId: number;
  severity: AlertSeverity;
  isRead: boolean;
  recipientId: string;
  dateGroup: string;
  relatedDateRange: RelatedDateRange | null;
}

export interface NotificationsSummary {
  critical?: number;
  warning?: number;
  success?: number;
  info?: number;
}

export interface NotificationsResponse {
  data: Notification[];
  totalItems: number;
  summary: NotificationsSummary;
}

export interface NotificationsQueryParams {
  page?: number;
  pageSize?: number;
  status?: NotificationStatus;
  machineIds?: number[];
  recipientIds?: string[];
}

export type { DateGroup };

export const DATE_GROUP_ORDER: DateGroup[] = [
  'today',
  'yesterday',
  'thisWeek',
  'thisMonth',
  'older'
];

export function isDateGroup(key: string): key is DateGroup {
  return (DATE_GROUP_ORDER as string[]).includes(key);
}

const DATE_GROUP_LABEL_KEYS: Record<DateGroup, string> = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this_week',
  thisMonth: 'this_month',
  older: 'older'
};

// The API also sends a `dateGroup`, but it is computed server-side and does not
// track the viewer's clock/timezone, so alerts from a past month can arrive
// labelled `thisMonth`. Derive the bucket locally from `alertedAt` instead.
export function getDateGroup(alertedAt: string): DateGroup {
  const date = moment(alertedAt);
  if (!date.isValid()) return 'older';
  const now = moment();
  if (date.isSame(now, 'day')) return 'today';
  if (date.isSame(moment(now).subtract(1, 'day'), 'day')) return 'yesterday';
  // `week` follows the active moment locale's first day of the week, which
  // differs per language: `es` merges onto moment/locale/es (Monday-start),
  // while `us` has no parent locale and falls back to moment's default
  // (Sunday-start). So a Sunday alert can read as "This Week" in English and
  // "This Month" in Spanish. That matches each language's calendar convention;
  // switch both to `isoWeek` if the boundary ever needs to be language-agnostic.
  if (date.isSame(now, 'week')) return 'thisWeek';
  if (date.isSame(now, 'month')) return 'thisMonth';
  return 'older';
}

export function getDateGroupLabel(group: string): string {
  return translate(DATE_GROUP_LABEL_KEYS[group as DateGroup] ?? 'older');
}

function severityToIconType(severity: AlertSeverity): AlertIconType {
  if (severity === 'critical') return 'siren';
  if (severity === 'success') return 'trending-up';
  return 'trending-down';
}

export function getNotificationLanguage(): 'english' | 'spanish' {
  const locale = (I18n as unknown as { _locale: string })._locale;
  return locale === 'es' ? 'spanish' : 'english';
}

export function mapNotificationToAlertCard(
  n: Notification,
  machineTitle: string
): AlertCardData {
  const lang = getNotificationLanguage();
  const dateGroup = getDateGroup(n.alertedAt);
  return {
    id: n.id,
    iconType: severityToIconType(n.severity),
    title: n.title[lang] ?? n.title.english,
    time:
      dateGroup === 'today'
        ? moment(n.alertedAt).format('h:mm A')
        : moment(n.alertedAt).format('YYYY-MM-DD h:mm A'),
    description: n.description[lang] ?? n.description.english,
    machineName: machineTitle,
    severity: n.severity,
    isRead: n.isRead,
    dateGroup,
    shiftId: n.relatedDateRange?.shiftId ?? null,
    shiftStart: n.relatedDateRange?.start ?? null,
    shiftEnd: n.relatedDateRange?.end ?? null
  };
}

// ---------------------------------------------------------------------------
// Notifications list — used by ViewAlerts page and the drawer panel.
// Refreshes every 5 min when the tab is active.
// ---------------------------------------------------------------------------

export const useNotifications = (
  params: NotificationsQueryParams = {},
  options?: { enabled?: boolean }
) => {
  return useQuery<NotificationsResponse>({
    queryKey: [NOTIFICATIONS_QUERY_KEY, params],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page != null) searchParams.append('page', String(params.page));
      if (params.pageSize != null)
        searchParams.append('pageSize', String(params.pageSize));
      if (params.status) searchParams.append('status', params.status);
      if (params.machineIds && params.machineIds.length > 0)
        params.machineIds.forEach((id) =>
          searchParams.append('machineIds', String(id))
        );
      if (params.recipientIds && params.recipientIds.length > 0)
        params.recipientIds.forEach((id) =>
          searchParams.append('recipientIds', id)
        );

      const qs = searchParams.toString();
      const { data } = await api.get<NotificationsResponse>(
        `/notifications${qs ? `?${qs}` : ''}`
      );
      return data;
    },
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5
  });
};

// ---------------------------------------------------------------------------
// Unread count — used by the bell badge in the top bar.
// Refreshes every minute. After the first successful fetch it passes `since`
// so the server only needs to count new arrivals. On invalidation the date
// is reset and the next call is treated as a first load again.
//
// The `since` date is stored inside the React Query cache under
// UNREAD_COUNT_SINCE_KEY so it shares the same lifecycle as the rest of the
// query state and is properly reset via removeQueries — no module-level
// mutable variables needed.
// ---------------------------------------------------------------------------

const UNREAD_COUNT_SINCE_KEY = [UNREAD_COUNT_QUERY_KEY, 'since'] as const;

export function useUnreadNotificationsCount() {
  const queryClient = useQueryClient();

  return useQuery<NotificationsResponse>({
    queryKey: [UNREAD_COUNT_QUERY_KEY],
    queryFn: async () => {
      const since =
        queryClient.getQueryData<string>(UNREAD_COUNT_SINCE_KEY) ?? null;
      const now = new Date().toISOString();

      const searchParams = new URLSearchParams();
      searchParams.append('status', 'unread');
      searchParams.append('pageSize', '1');
      if (since) searchParams.append('since', since);

      const { data } = await api.get<NotificationsResponse>(
        `/notifications?${searchParams.toString()}`
      );

      // Store the fetch time so the next poll can use it as `since`.
      // Nothing observes this key, so setQueryData causes no re-renders.
      queryClient.setQueryData(UNREAD_COUNT_SINCE_KEY, now);

      if (since) {
        // Delta poll: add new arrivals to the existing cached total instead
        // of replacing it. getQueryData reads the pre-update cached value.
        const cached = queryClient.getQueryData<NotificationsResponse>([
          UNREAD_COUNT_QUERY_KEY
        ]);
        return {
          ...data,
          totalItems: (cached?.totalItems ?? 0) + data.totalItems
        };
      }

      return data;
    },
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 1000 * 60 * 5
  });
}

// ---------------------------------------------------------------------------
// Mark as read — invalidates both query families.
// ---------------------------------------------------------------------------

function useInvalidateNotifications() {
  const queryClient = useQueryClient();

  return () => {
    // Clear the stored `since` date so the next unread-count fetch is
    // treated as a first load (returns the full unread total, not a delta).
    queryClient.removeQueries({ queryKey: UNREAD_COUNT_SINCE_KEY });
    queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
  };
}

export const useMarkNotificationsRead = () => {
  const invalidate = useInvalidateNotifications();

  return useMutation<void, AxiosError, string[]>({
    mutationFn: async (notificationIds) => {
      await api.patch('/notifications/markRead', { notificationIds });
    },
    onSuccess: invalidate
  });
};

// Marks every notification of the logged-in user as read server-side, not
// just the ones loaded on the current page.
export const useMarkAllNotificationsRead = () => {
  const invalidate = useInvalidateNotifications();

  return useMutation<void, AxiosError, void>({
    mutationFn: async () => {
      await api.patch('/notifications/markAllRead');
    },
    onSuccess: invalidate
  });
};
